// src/hooks/useFeatureGate.ts
/**
 * React hook for consuming the /api/billing/features/ endpoint.
 *
 * Provides:
 *  - `features`   — tier-level boolean feature flags
 *  - `quotas`     — tier-level numeric limits
 *  - `beta`       — whether beta mode is active + user is allowed
 *  - `tier`       — { name, display_name }
 *  - `canUse(feature)` — convenience helper that accounts for both
 *                         beta gate and tier feature flag
 *  - `loading` / `error` / `refetch`
 *
 * Beta Tier Simulation:
 *  Beta users can activate a "Simulate Tier" mode that masks the real
 *  features/quotas with those of a chosen tier, so they can experience
 *  the product as a Free / Starter / Professional user would.
 *  The simulation is stored in localStorage and only affects the frontend;
 *  the backend still grants full beta access.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../api/api";
import { useAuth } from "./useAuth";
import logger from "utils/logger";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeatureFlags {
  edit_records: boolean;
  ai_extraction: boolean;
  voice: boolean;
  export_csv: boolean;
  export_api: boolean;
  api_access: boolean;
  cross_org_share: boolean;
  bulk_upload: boolean;
  analytics_view: boolean;
  analytics_filter: boolean;
  sso: boolean;
  audit_logs: boolean;
  priority_support: boolean;
  [key: string]: boolean; // allow dynamic lookups
}

export interface FeatureQuotas {
  max_patients: number;
  max_documents_per_month: number;
  max_tokens_per_month: number;
  max_voice_minutes_per_month: number;
  max_users: number;
  max_environments: number;
  max_bulk_upload_patients: number;
  [key: string]: number;
}

export interface BetaStatus {
  active: boolean;
  user_in_beta: boolean;
  is_founder?: boolean;
  is_beta_tester?: boolean;
  beta_expires_at?: string | null;
}

export interface TierInfo {
  name: string;
  display_name: string;
}

export interface TutorialAllowance {
  enabled: boolean;
  used: number;
  limit: number;
  remaining: number;
}

export interface FeatureGateData {
  beta: BetaStatus;
  tier: TierInfo;
  features: FeatureFlags;
  quotas: FeatureQuotas;
  subscription_status: string;
  is_active: boolean;
  tutorial_allowance?: TutorialAllowance;
}

/** A tier definition for frontend simulation (mirrors backend SubscriptionTier fields). */
export interface SimulatableTier {
  name: string;
  display_name: string;
  features: FeatureFlags;
  quotas: FeatureQuotas;
}

// Default empty state
const EMPTY_FEATURES: FeatureFlags = {
  edit_records: false,
  ai_extraction: false,
  voice: false,
  export_csv: false,
  export_api: false,
  api_access: false,
  cross_org_share: false,
  bulk_upload: false,
  analytics_view: true,
  analytics_filter: false,
  sso: false,
  audit_logs: false,
  priority_support: false,
};

const EMPTY_QUOTAS: FeatureQuotas = {
  max_patients: 1,
  max_documents_per_month: 0,
  max_tokens_per_month: 0,
  max_voice_minutes_per_month: 0,
  max_users: 1,
  max_environments: 0,
  max_bulk_upload_patients: 0,
};

// ---------------------------------------------------------------------------
// Tier simulation presets (mirrors backend SubscriptionTier defaults)
// ---------------------------------------------------------------------------

const SIMULATED_TIERS: Record<string, SimulatableTier> = {
  free: {
    name: "free",
    display_name: "Free",
    features: {
      edit_records: true,
      ai_extraction: false,
      voice: false,
      export_csv: false,
      export_api: false,
      api_access: false,
      cross_org_share: false,
      bulk_upload: false,
      analytics_view: true,
      analytics_filter: false,
      sso: false,
      audit_logs: false,
      priority_support: false,
    },
    quotas: {
      max_patients: 1,
      max_documents_per_month: 5,
      max_tokens_per_month: 0,
      max_voice_minutes_per_month: 0,
      max_users: 1,
      max_environments: 1,
      max_bulk_upload_patients: 0,
    },
  },
  starter: {
    name: "starter",
    display_name: "Starter",
    features: {
      edit_records: true,
      ai_extraction: true,
      voice: false,
      export_csv: true,
      export_api: false,
      api_access: false,
      cross_org_share: false,
      bulk_upload: false,
      analytics_view: true,
      analytics_filter: false,
      sso: false,
      audit_logs: false,
      priority_support: false,
    },
    quotas: {
      max_patients: 25,
      max_documents_per_month: 100,
      max_tokens_per_month: 500000,
      max_voice_minutes_per_month: 0,
      max_users: 3,
      max_environments: 1,
      max_bulk_upload_patients: 0,
    },
  },
  professional: {
    name: "professional",
    display_name: "Professional",
    features: {
      edit_records: true,
      ai_extraction: true,
      voice: true,
      export_csv: true,
      export_api: true,
      api_access: true,
      cross_org_share: false,
      bulk_upload: true,
      analytics_view: true,
      analytics_filter: true,
      sso: false,
      audit_logs: true,
      priority_support: true,
    },
    quotas: {
      max_patients: 200,
      max_documents_per_month: 1000,
      max_tokens_per_month: 5000000,
      max_voice_minutes_per_month: 120,
      max_users: 15,
      max_environments: 1,
      max_bulk_upload_patients: 50,
    },
  },
  team: {
    name: "team",
    display_name: "Team",
    features: {
      edit_records: true,
      ai_extraction: true,
      voice: true,
      export_csv: true,
      export_api: true,
      api_access: true,
      cross_org_share: true,
      bulk_upload: true,
      analytics_view: true,
      analytics_filter: true,
      sso: false,
      audit_logs: false,
      priority_support: true,
    },
    quotas: {
      max_patients: 0, // 0 = unlimited
      max_documents_per_month: 500,
      max_tokens_per_month: 500000,
      max_voice_minutes_per_month: 120,
      max_users: 5,
      max_environments: 5,
      max_bulk_upload_patients: 50,
    },
  },
};

const SIMULATION_STORAGE_KEY = "beta-tier-simulation";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

// Joan, 2026-05-27: system admins / superusers bypass all tier quotas and
// feature flags. The backend already grants them full access; the frontend
// gate must mirror that so the UI doesn't show locks on operations the
// backend will happily accept.
const ADMIN_QUOTAS: FeatureQuotas = {
  max_patients: Number.POSITIVE_INFINITY,
  max_documents_per_month: Number.POSITIVE_INFINITY,
  max_tokens_per_month: Number.POSITIVE_INFINITY,
  max_voice_minutes_per_month: Number.POSITIVE_INFINITY,
  max_users: Number.POSITIVE_INFINITY,
  max_environments: Number.POSITIVE_INFINITY,
  max_bulk_upload_patients: Number.POSITIVE_INFINITY,
};

export const useFeatureGate = () => {
  const { isAuthenticated, user } = useAuth();
  const [data, setData] = useState<FeatureGateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdminBypass = user?.is_system_admin === true;

  // Beta tier simulation state
  const [simulatedTierName, setSimulatedTierNameState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SIMULATION_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const fetchFeatures = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get("/billing/features/");
      setData(response.data);
      setError(null);
    } catch (err: any) {
      // Non-critical — default to empty features (free tier)
      logger.warn("[useFeatureGate] Failed to fetch features:", err);
      setError(err.response?.data?.error || "Failed to fetch feature flags");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // Raw values from backend
  const rawBeta = data?.beta ?? { active: false, user_in_beta: false };
  const isBetaUser = rawBeta.user_in_beta;

  // Determine if simulation is active
  const simulatedTier = isBetaUser && simulatedTierName
    ? SIMULATED_TIERS[simulatedTierName] ?? null
    : null;
  const isSimulating = simulatedTier !== null;
  const isFounder = rawBeta.is_founder ?? false;

  // Apply simulation mask (or use real data).
  // Features are always masked during simulation so founders can test feature gating.
  // Quotas are only masked for non-founders; founders keep real (inflated) quotas
  // from the backend so that frontend-side quota checks don't falsely block them
  // — the backend is the authoritative enforcement point for founders.
  // System admins / superusers ALWAYS bypass — Infinity quotas, all features on —
  // unless they are currently simulating a tier (in which case they want to
  // experience the gating). Joan, 2026-05-27.
  const ADMIN_FEATURES_ALL_TRUE: FeatureFlags = useMemo(() => {
    const acc: FeatureFlags = { ...EMPTY_FEATURES };
    Object.keys(acc).forEach((k) => {
      acc[k] = true;
    });
    return acc;
  }, []);

  const features =
    isAdminBypass && !isSimulating
      ? ADMIN_FEATURES_ALL_TRUE
      : isSimulating
        ? simulatedTier.features
        : data?.features ?? EMPTY_FEATURES;
  const quotas =
    isAdminBypass && !isSimulating
      ? ADMIN_QUOTAS
      : isSimulating && !isFounder
        ? simulatedTier.quotas
        : data?.quotas ?? EMPTY_QUOTAS;
  const beta = rawBeta;
  const tier: TierInfo = isSimulating
    ? { name: simulatedTier.name, display_name: `${simulatedTier.display_name} (Simulated)` }
    : data?.tier ?? { name: "free", display_name: "Free" };

  /**
   * Check if the current user can use a specific feature.
   * Accounts for:
   *  1. Beta simulation — use simulated features
   *  2. Beta mode — if active and user is NOT in beta, block everything
   *  3. Tier feature flag — must be true
   */
  const canUse = useCallback(
    (feature: keyof FeatureFlags | string): boolean => {
      // System admins bypass beta + tier gates (unless they're simulating).
      if (isAdminBypass && !isSimulating) return true;
      // If simulating, use simulated features
      if (isSimulating) return !!features[feature];
      // If beta mode is active and user is NOT in the programme, block
      if (beta.active && !beta.user_in_beta) return false;
      // Check tier-level flag
      return !!features[feature];
    },
    [beta, features, isAdminBypass, isSimulating]
  );

  /**
   * Returns true when the user is blocked from token-spending actions
   * because beta mode is active and they are not in the beta programme.
   */
  const isBetaBlocked = useMemo(
    () =>
      !isAdminBypass &&
      !isSimulating &&
      beta.active &&
      !beta.user_in_beta,
    [beta, isAdminBypass, isSimulating],
  );

  // -------------------------------------------------------------------------
  // Simulation controls (only usable by beta users)
  // -------------------------------------------------------------------------

  /** Start simulating a specific tier. Pass null to stop simulating. */
  const setSimulatedTier = useCallback(
    (tierName: string | null) => {
      if (!isBetaUser) return; // Guard: only beta users
      setSimulatedTierNameState(tierName);
      try {
        if (tierName) {
          localStorage.setItem(SIMULATION_STORAGE_KEY, tierName);
        } else {
          localStorage.removeItem(SIMULATION_STORAGE_KEY);
        }
      } catch {
        // localStorage unavailable — no-op
      }
    },
    [isBetaUser]
  );

  /** List of tiers available for simulation. */
  const simulatableTiers = useMemo(
    () => Object.values(SIMULATED_TIERS),
    []
  );

  // Tutorial extraction allowance
  const tutorialAllowance: TutorialAllowance = data?.tutorial_allowance ?? {
    enabled: false,
    used: 0,
    limit: 0,
    remaining: 0,
  };

  return {
    // Raw data
    data,
    features,
    quotas,
    beta,
    tier,
    // Convenience
    canUse,
    isBetaBlocked,
    isActive: data?.is_active ?? false,
    subscriptionStatus: data?.subscription_status ?? "active",
    // Tutorial allowance
    tutorialAllowance,
    // State
    loading,
    error,
    refetch: fetchFeatures,
    // Beta tier simulation
    isBetaUser,
    isSimulating,
    simulatedTierName,
    setSimulatedTier,
    simulatableTiers,
  };
};

export default useFeatureGate;
